import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Attention, PaginationControls, Typography } from "@optimizely/axiom";
import { Flags } from "../../../../feature-flags/flags";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag/useFeatureFlag";
import { useDebounce } from "../../../../hooks/useDebounce/useDebounce";
import { useDeveloperSettingsSearch } from "../../../../hooks/useDeveloperSettingsSearch/useDeveloperSettingsSearch";
import { DEFAULT_PAGE_SIZE } from "../../../../constants/constants";
import { SearchInput } from "../../../components/SearchInput/SearchInput";
import { ResetFilterPrompt } from "../../../components/ResetFilterPrompt/ResetFilterPrompt";
import { DeveloperSettingsTable } from "../../../components/DeveloperSettingsTable/DeveloperSettingsTable";

const SEARCH_DEBOUNCE_MS = 300;

export const DeveloperSettings = () => {
    const navigate = useNavigate();
    const { enabled: showSupportPage } = useFeatureFlag(Flags.ENABLE_SUPPORT_FEATURES);
    const { enabled: developerSettingsEnabled } = useFeatureFlag(Flags.ENABLE_DEVELOPER_SETTINGS);

    const {
        value: searchValue,
        debouncedValue: searchQuery,
        setValue: setSearchValue
    } = useDebounce<string>("", SEARCH_DEBOUNCE_MS);
    const [currentPage, setCurrentPage] = useState<number>(1);

    // Debounce while typing, but treat an empty input as an immediate clear: otherwise clearing the
    // field (reset action or manual delete) would keep filtering for the debounce delay.
    const effectiveSearch = searchValue.trim() === "" ? "" : searchQuery;

    useEffect(() => {
        if (!showSupportPage) {
            navigate("/404");
        }
    }, [navigate, showSupportPage]);

    // When the (effective) search term changes, jump back to the first page so results aren't hidden
    // on a now-out-of-range page.
    useEffect(() => {
        setCurrentPage(1);
    }, [effectiveSearch]);

    const {
        organizations,
        developerSettingsByOrg,
        totalMatched,
        totalOrganizations,
        loading,
        error,
        updateDeveloperSettings
    } = useDeveloperSettingsSearch({
        search: effectiveSearch,
        page: currentPage,
        pageSize: DEFAULT_PAGE_SIZE,
        // Gate the fan-out on the flag so no developer-settings requests fire when the feature is off.
        enabled: developerSettingsEnabled
    });

    const totalPages = Math.ceil(totalMatched / DEFAULT_PAGE_SIZE);

    // Keep the current page in range. The filtered set can shrink underneath us (e.g. updating a vendor
    // name while a vendor search is active), which would otherwise strand the user on an empty page.
    useEffect(() => {
        if (totalPages > 0 && currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    // Defensive: the route is already gated by this flag, but never render the section when the flag is
    // off (the hook above also receives enabled=false, so no settings requests are made).
    if (!developerSettingsEnabled) {
        return null;
    }

    // A failed load must not fall through to the states below: zero organizations because the request
    // failed is not the same as zero organizations existing, and a failed settings fan-out would render
    // every row as "developer access off / no vendor" — wrong data a support engineer could act on.
    // Mirrors the org-side Settings > Organization page.
    if (error) {
        return (
            <Attention className="push-double--ends" data-testid="developer-settings-error" type="bad-news">
                We couldn&apos;t load developer settings. Please refresh the page and try again.
            </Attention>
        );
    }

    const showPagination = totalMatched > DEFAULT_PAGE_SIZE;

    // Gate the table on the total match count, not the current page slice: a transiently out-of-range
    // page would otherwise render neither a table nor an empty state, leaving a blank area.
    const hasResults = totalMatched > 0;
    const noOrganizationsExist = !loading && totalOrganizations === 0;
    const noFilterMatches = !loading && totalOrganizations > 0 && totalMatched === 0;

    return (
        <>
            <div className="flex flex--row flex-justified--between push-triple--ends">
                <div className="width--1-4">
                    <SearchInput
                        placeholder="Search organizations by name or vendor..."
                        type="text"
                        value={searchValue}
                        onChange={({ value }) => setSearchValue(value)}
                    />
                </div>
            </div>

            {noOrganizationsExist && (
                <Typography type="body" data-testid="no-organizations-empty" className="push-double--ends">
                    No organizations found.
                </Typography>
            )}

            {noFilterMatches && (
                <ResetFilterPrompt
                    data-testid="no-filter-matches-empty"
                    onClick={() => setSearchValue("")}
                    prompt="No organizations match your search."
                />
            )}

            {(loading || hasResults) && (
                <DeveloperSettingsTable
                    organizations={organizations}
                    developerSettingsByOrg={developerSettingsByOrg}
                    loading={loading}
                    onUpdate={updateDeveloperSettings}
                />
            )}

            {showPagination && (
                <PaginationControls
                    className="anchor--bottom"
                    currentPage={currentPage}
                    goToPage={(page: number) => setCurrentPage(page)}
                    totalPages={totalPages}
                />
            )}
        </>
    );
};

DeveloperSettings.displayName = "DeveloperSettings";
